import { useEffect, useId, useRef, useState } from 'react'
import { useSearchParams } from 'react-router'
import {
  Controller,
  useFieldArray,
  useForm,
  type Control,
  type FieldErrors,
  type UseFormSetValue,
  type UseFormWatch,
} from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  AlertTriangle,
  ArrowLeft,
  AudioLines,
  Bell,
  BookOpen,
  Briefcase,
  Calendar,
  ChevronRight,
  Edit2,
  Plug,
  History,
  MessageSquare,
  Mic,
  Phone,
  PhoneForwarded,
  Plus,
  Trash2,
  Table2,
  Target,
  User,
  Zap,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import { useProfile } from '@/api/profile'
import {
  useAvailableAIPhoneNumbers,
  useCreateAIAgent,
  useElevenLabsVoices,
  useExpressiveVoices,
  useSmsFromNumbers,
  useUpdateAIAgent,
  type AIAgent,
  type ElevenLabsVoice,
  type ExpressiveVoice,
  type SmsFromNumberOption,
} from '@/api/aiAgents'
import { KnowledgeTabContent } from './KnowledgeTabContent'
import { VoiceSelectionModal } from './VoiceSelectionModal'
import { ExpressiveVoiceModal } from './ExpressiveVoiceModal'
import { PhoneNumberSelectionModal } from './PhoneNumberSelectionModal'
import { SMSActionModal } from './SMSActionModal'
import { TransferActionModal } from './TransferActionModal'
import { NotifyAdminModal } from './NotifyAdminModal'
import { LookupSection } from './LookupSection'
import { ConnectedSoftwareView } from '@/components/manager/connectedSoftware/ConnectedSoftwareView'
import { CapaciteRow } from './CapaciteRow'
import { AgentRevisionsSheet } from './AgentRevisionsSheet'
import { useLookupTables } from '@/api/lookupTables'
import { useAIAgentMissions } from '@/api/aiAgents'
import { MissionsSection } from './MissionsSection'
import { TestCallModal } from './TestCallModal'
import { GreetingPreview } from './GreetingPreview'
import { CalendarBookingConfig } from './CalendarBookingConfig'
import { defaultWorkingHours } from './WorkingHoursEditor'
import { formSchema, type FormValues } from './formSchema'
import type {
  NotifyAdminAction,
  SMSAction,
  TransferAction,
} from './actionsSchemas'

type TabType = 'behavior' | 'knowledge' | 'actions' | 'software'

/** Nom d'agent tronqué pour le bouton « Tester » ; le nom complet vit dans le title. */
function truncateName(name: string, max = 22): string {
  return name.length > max ? `${name.slice(0, max - 1).trimEnd()}…` : name
}

const DEFAULT_FIRST_MESSAGE =
  "Bonjour, je suis l'assistant virtuel. Comment puis-je vous aider?"



// Normalise des horaires hérités vers la structure complète 7 jours exigée par
// le schéma Zod. Indispensable : les anciens agents ont `calendar_working_hours`
// à `{}` (ou partiel) en base. Un simple `?? defaultWorkingHours()` ne rattrape
// PAS un objet vide (`??` ne cible que null/undefined) -> l'objet incomplet
// passe, le schéma le rejette, et TOUTE sauvegarde de l'agent echoue avec une
// erreur trompeuse pointant sur l'onglet Comportement (champ non mappe). On
// fusionne donc chaque jour stocke par-dessus le defaut.
function normalizeWorkingHours(raw: unknown): FormValues['calendar_working_hours'] {
  const def = defaultWorkingHours()
  if (!raw || typeof raw !== 'object') return def
  const stored = raw as Record<string, Partial<{ enabled: boolean; start: string; end: string }>>
  const out = {} as FormValues['calendar_working_hours']
  for (const day of Object.keys(def) as (keyof typeof def)[]) {
    out[day] = { ...def[day], ...(stored[day] ?? {}) }
  }
  return out
}

function agentToDefaults(agent?: AIAgent | null): FormValues {
  const a = agent as (AIAgent & Record<string, unknown>) | null | undefined
  return {
    name: a?.name ?? '',
    language: (a?.language as FormValues['language']) ?? 'fr',
    voice_id: a?.voice_id ?? '',
    mode_expressif: a?.mode_expressif ?? false,
    // The default voice is not a choice: kept empty so that it is never sent back.
    voix_expressive:
      a?.voix_expressive && !a.voix_expressive.par_defaut ? a.voix_expressive.id : '',
    call_mode: a?.call_mode ?? 'inbound',
    phone_number_id: a?.uses_company_main_number
      ? 'main'
      : a?.phone_number != null
        ? String(a.phone_number)
        : '',
    sms_from_number: a?.sms_from_number ?? '',
    first_message: a?.first_message ?? DEFAULT_FIRST_MESSAGE,
    system_prompt: a?.system_prompt ?? '',
    business_context: a?.business_context ?? '',
    knowledge_web_urls: (a?.knowledge_web_urls ?? []).map((url) => ({ url })),
    knowledge_text: a?.knowledge_text ?? '',
    sms_actions: (a?.sms_actions ?? []) as SMSAction[],
    transfer_actions: (a?.transfer_actions ?? []) as TransferAction[],
    notify_admin_actions: (a?.notify_admin_actions ?? []) as NotifyAdminAction[],
    // Calendar
    google_calendar: (a?.google_calendar ?? null) as number | null,
    microsoft_calendar: (a?.microsoft_calendar as number | null | undefined) ?? null,
    square_booking: (a?.square_booking as number | null | undefined) ?? null,
    wix_booking: (a?.wix_booking as number | null | undefined) ?? null,
    calendar_booking_duration: (a?.calendar_booking_duration as number) ?? 30,
    calendar_buffer_time: (a?.calendar_buffer_time as number) ?? 15,
    calendar_default_appointment_type:
      (a?.calendar_default_appointment_type as FormValues['calendar_default_appointment_type']) ??
      'in_person',
    calendar_allow_client_type_choice:
      (a?.calendar_allow_client_type_choice as boolean) ?? false,
    calendar_intake_info: (a?.calendar_intake_info as string) ?? '',
    calendar_deposit_link: (a?.calendar_deposit_link as boolean) ?? false,
    calendar_working_hours: normalizeWorkingHours(a?.calendar_working_hours),
    calendar_confirmation_method:
      (a?.calendar_confirmation_method as FormValues['calendar_confirmation_method']) ??
      'both',
    calendar_notify_company: (a?.calendar_notify_company as boolean) ?? true,
    calendar_company_notification_method:
      (a?.calendar_company_notification_method as FormValues['calendar_company_notification_method']) ??
      'email',
    calendar_company_notification_email:
      (a?.calendar_company_notification_email as string) ?? '',
    calendar_company_notification_phone:
      (a?.calendar_company_notification_phone as string) ?? '',
    can_find_appointments: (a?.can_find_appointments as boolean) ?? false,
    can_cancel_appointments: a?.can_cancel_appointments ?? false,
    can_reschedule_appointments: a?.can_reschedule_appointments ?? false,
    calendar_cancellation_email_subject:
      (a?.calendar_cancellation_email_subject as string) ?? '',
    calendar_cancellation_email_body:
      (a?.calendar_cancellation_email_body as string) ?? '',
    calendar_reschedule_email_subject:
      (a?.calendar_reschedule_email_subject as string) ?? '',
    calendar_reschedule_email_body:
      (a?.calendar_reschedule_email_body as string) ?? '',
    calendar_reschedule_sms_template:
      (a?.calendar_reschedule_sms_template as string) ?? '',
    calendar_sms_template: (a?.calendar_sms_template as string) ?? '',
  }
}

interface Props {
  agent?: AIAgent | null
  initialTab?: TabType
  onSuccess: () => void
  onCancel: () => void
}

/**
 * Formulaire AI Agent (Phase 4b + 4c).
 * Stack : React Hook Form + Zod + useFieldArray ; TanStack Query pour les mutations.
 */
export function AIAgentFormView({ agent, initialTab, onSuccess, onCancel }: Props) {
  const { t } = useTranslation()
  const { data: profile } = useProfile()
  const companyId = profile?.company

  const { data: allVoices = [] } = useElevenLabsVoices()
  const { data: expressiveData } = useExpressiveVoices()
  const expressiveVoices = expressiveData?.voix ?? []
  const { data: phoneData } = useAvailableAIPhoneNumbers(companyId, agent?.id)
  const phoneNumbers = phoneData?.phone_numbers ?? []
  const companyMainNumber = phoneData?.company_main_number ?? null
  // Numéros utilisables comme expéditeur SMS (uniquement dispo en mode édition).
  const { data: smsFromNumbers = [] } = useSmsFromNumbers(companyId, agent?.id)

  const createAgent = useCreateAIAgent(companyId)
  const updateAgent = useUpdateAIAgent(companyId, agent?.id ?? 0)

  // Onglet actif synchronisé à ?agentTab=behavior|knowledge|actions|...
  // Important : on set TOUJOURS le param explicitement (jamais delete). Sinon,
  // si `initialTab` est passé en prop (ex: 'actions' pour agent incomplet),
  // cliquer sur 'behavior' supprime le param et activeTab retombe sur
  // `initialTab` au lieu de devenir 'behavior' — clic ignoré.
  const [searchParams, setSearchParams] = useSearchParams()
  const paramTab = searchParams.get('agentTab') as TabType | null
  const activeTab: TabType = paramTab ?? initialTab ?? 'behavior'
  const setActiveTab = (next: TabType) => {
    const params = new URLSearchParams(searchParams)
    params.set('agentTab', next)
    setSearchParams(params)
  }
  const [isVoiceModalOpen, setIsVoiceModalOpen] = useState(false)
  const [isPhoneModalOpen, setIsPhoneModalOpen] = useState(false)
  // State des sous-modals : null = fermé ; objet = édition ; 'new' = création.
  type EditState<T> = T | 'new' | null
  const [editingSMS, setEditingSMS] = useState<EditState<SMSAction>>(null)
  const [editingTransfer, setEditingTransfer] = useState<EditState<TransferAction>>(null)
  const [editingNotify, setEditingNotify] = useState<EditState<NotifyAdminAction>>(null)
  // « Tester » ouvre la modale d'appel immédiatement ; la sauvegarde court
  // en arrière-plan et la modale en affiche l'avancement (cf. onSaveAndTest).
  const [showTestCall, setShowTestCall] = useState(false)
  const [testSaveState, setTestSaveState] = useState<'saving' | 'saved' | 'error'>('saving')
  // Valeurs du clic, pour que « Réessayer » rejoue exactement la même
  // sauvegarde même si le formulaire a bougé entre-temps.
  const testValuesRef = useRef<FormValues | null>(null)
  // Just created by the creation assistant, opened on "Test the agent" (?tester=1):
  // nothing to save first, the test call opens at once.
  useEffect(() => {
    if (!agent || searchParams.get('tester') !== '1') return
    setTestSaveState('saved')
    setShowTestCall(true)
    const params = new URLSearchParams(searchParams)
    params.delete('tester')
    setSearchParams(params, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent?.id])

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: agentToDefaults(agent),
  })
  const { control, watch, setValue, handleSubmit, formState } = form

  // Language the voice was last checked against (see the voice effect below).
  const previousLanguage = useRef<string>(agentToDefaults(agent).language)

  // Re-hydrate le form si on passe d'un agent à un autre sans démontrer le composant.
  useEffect(() => {
    form.reset(agentToDefaults(agent))
    previousLanguage.current = agentToDefaults(agent).language
  }, [agent, form])

  const language = watch('language')
  const voiceId = watch('voice_id')
  const modeExpressif = watch('mode_expressif')
  const voixExpressive = watch('voix_expressive')
  const phoneNumberId = watch('phone_number_id')

  const smsActions = useFieldArray({ control, name: 'sms_actions' })
  // Onglet Actions : une capacité ouverte à la fois.
  // ?capacite=calendrier (link from the iPhone app) opens that row on arrival.
  const [capacite, setCapacite] = useState<string | null>(() => searchParams.get('capacite'))
  const [historique, setHistorique] = useState(false)
  const basculer = (c: string) => setCapacite((x) => (x === c ? null : c))
  const { data: missionsAgent = [] } = useAIAgentMissions(companyId, agent?.id)
  const { data: tablesAll } = useLookupTables(companyId)
  const tablesAgent = (tablesAll || []).filter((tb) => agent?.id && tb.agent_ids.includes(agent.id))
  const transferActions = useFieldArray({ control, name: 'transfer_actions' })
  const notifyActions = useFieldArray({ control, name: 'notify_admin_actions' })

  // Filtre les voix par langue (label.language) ; pas de label = compatible tout.
  // Bilingue (« multi ») : les voix françaises et anglaises, toutes tiennent les
  // deux langues avec ces modèles ; l'accent affiché dit le reste.
  const voixCompatible = (voice: ElevenLabsVoice) => {
    const l = voice.labels?.language
    if (!l) return true
    if (language === 'multi' || language === 'fr-en' || language === 'en-fr') return l === 'fr' || l === 'en'
    return l === language
  }
  // Offered voices for this language. A language with none offered (es, de) gets them
  // all: they speak it too. Plus this agent's own voice when it is no longer offered.
  const offeredVoices = allVoices.filter((v) => !v.en_place)
  const offeredForLanguage = offeredVoices.filter(voixCompatible)
  const pickableVoices = offeredForLanguage.length ? offeredForLanguage : offeredVoices
  const ownVoice = allVoices.find((v) => v.en_place && v.voice_id === voiceId)
  const voices = ownVoice ? [ownVoice, ...pickableVoices] : pickableVoices
  // A voice the list does not show (a cloned one) keeps a neutral name on the card.
  const selectedVoice: ElevenLabsVoice | undefined =
    allVoices.find((v) => v.voice_id === voiceId) ??
    (voiceId && allVoices.length ? { voice_id: voiceId, name: t('manager.aiAgents.customVoice') } : undefined)
  // The voice really used in expressive mode: the one picked, an older voice kept
  // as is by the server, or the default voice.
  const selectedExpressiveVoice: ExpressiveVoice | undefined = voixExpressive
    ? expressiveVoices.find((v) => v.id === voixExpressive) ??
      (agent?.voix_expressive?.id === voixExpressive ? agent.voix_expressive : undefined)
    : expressiveData?.par_defaut ?? (agent?.voix_expressive?.par_defaut ? agent.voix_expressive : undefined)

  // Never replace an agent's voice behind its back. A voice is picked only for a new
  // agent (none yet), or when the user switches to a language the current voice does
  // not speak: the default voice of that language (Perle, Sarah).
  useEffect(() => {
    if (!allVoices.length) return
    const languageChanged = previousLanguage.current !== language
    previousLanguage.current = language
    const current = allVoices.find((v) => v.voice_id === voiceId)
    if (voiceId && (!languageChanged || !current || voixCompatible(current))) return
    const primary = language.startsWith('en') ? 'en' : 'fr'
    const fallback =
      pickableVoices.find((v) => v.par_defaut && v.labels?.language === primary) ??
      pickableVoices.find((v) => v.par_defaut) ??
      pickableVoices[0]
    if (fallback && fallback.voice_id !== voiceId) {
      setValue('voice_id', fallback.voice_id, { shouldDirty: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language, allVoices, voiceId, setValue])

  const buildPayload = (values: FormValues) => {
    // Payload 1:1 avec ce qu'attend le backend.
    // - call_mode=outbound → on n'envoie jamais phone_number_id
    // - phone_number_id 'none' → null (libère le numéro côté backend en édition)
    // - phone_number_id vide → omis (pas de changement)
    const payload: Record<string, unknown> = {
      name: values.name,
      language: values.language,
      voice_id: values.voice_id,
      call_mode: values.call_mode,
      first_message: values.first_message,
      system_prompt: values.system_prompt,
      business_context: values.business_context,
      knowledge_web_urls: values.knowledge_web_urls.map((u) => u.url),
      knowledge_text: values.knowledge_text,
      sms_actions: values.sms_actions,
      transfer_actions: values.transfer_actions,
      notify_admin_actions: values.notify_admin_actions,
      google_calendar: values.google_calendar,
      microsoft_calendar: values.microsoft_calendar,
      square_booking: values.square_booking,
      wix_booking: values.wix_booking,
      calendar_booking_duration: values.calendar_booking_duration,
      calendar_buffer_time: values.calendar_buffer_time,
      calendar_default_appointment_type: values.calendar_default_appointment_type,
      calendar_allow_client_type_choice: values.calendar_allow_client_type_choice,
      calendar_intake_info: values.calendar_intake_info,
      calendar_deposit_link: values.calendar_deposit_link,
      calendar_working_hours: values.calendar_working_hours,
      calendar_confirmation_method: values.calendar_confirmation_method,
      calendar_notify_company: values.calendar_notify_company,
      calendar_company_notification_method: values.calendar_company_notification_method,
      calendar_company_notification_email: values.calendar_company_notification_email,
      calendar_company_notification_phone: values.calendar_company_notification_phone,
      can_find_appointments: values.can_find_appointments,
      can_cancel_appointments: values.can_cancel_appointments,
      can_reschedule_appointments: values.can_reschedule_appointments,
      calendar_cancellation_email_subject: values.calendar_cancellation_email_subject,
      calendar_cancellation_email_body: values.calendar_cancellation_email_body,
      calendar_reschedule_email_subject: values.calendar_reschedule_email_subject,
      calendar_reschedule_email_body: values.calendar_reschedule_email_body,
      calendar_reschedule_sms_template: values.calendar_reschedule_sms_template,
      calendar_sms_template: values.calendar_sms_template,
      // Numéro expéditeur SMS (string vide = null côté backend = pas de SMS possible)
      sms_from_number: values.sms_from_number || null,
      // Merged by the server; never the whole template_config, which would replace it.
      mode_expressif: values.mode_expressif,
    }
    if (values.voix_expressive) payload.voix_expressive = values.voix_expressive

    if (values.call_mode !== 'outbound') {
      if (values.phone_number_id === 'none') {
        payload.phone_number_id = null
      } else if (values.phone_number_id) {
        payload.phone_number_id = values.phone_number_id
      }
    }

    return payload
  }

  const onSubmit = async (values: FormValues) => {
    const payload = buildPayload(values)
    try {
      if (agent) {
        await updateAgent.mutateAsync(payload)
        toast.success(t('manager.aiAgents.agentModifiedSuccess'))
      } else {
        await createAgent.mutateAsync(
          payload as unknown as Parameters<typeof createAgent.mutateAsync>[0],
        )
        toast.success(t('manager.aiAgents.agentCreatedSuccessDesc'))
      }
      onSuccess()
    } catch {
      toast.error(t('manager.aiAgents.unableToCreateAgent'))
    }
  }

  /**
   * « Tester » : la modale s'ouvre à l'instant du clic et la sauvegarde
   * part en arrière-plan. L'enregistrement (écriture + préparation de la
   * config vocale) prend plusieurs secondes : plutôt que de figer le bouton,
   * on laisse le client saisir son numéro et choisir sa mission pendant ce
   * temps — l'attente disparaît au lieu d'être décorée. La modale bloque
   * l'appel tant que l'étape n'est pas verte, et affiche l'échec sur place
   * avec une relance (pas de toast qui s'évapore).
   *
   * L'enregistrement est d'office : tester une version modifiée mais non
   * enregistrée donnerait un résultat faux sans que rien ne l'explique.
   */
  const runTestSave = async (values: FormValues) => {
    setTestSaveState('saving')
    try {
      await updateAgent.mutateAsync(buildPayload(values))
      setTestSaveState('saved')
    } catch {
      setTestSaveState('error')
    }
  }

  const onSaveAndTest = (values: FormValues) => {
    if (!agent) return
    testValuesRef.current = values
    setShowTestCall(true)
    void runTestSave(values)
  }

  const tabs: Array<{
    id: TabType
    label: string
    icon: typeof Briefcase
    disabled?: boolean
  }> = [
    { id: 'behavior', label: t('manager.aiAgents.behaviorTab'), icon: Briefcase },
    { id: 'knowledge', label: t('manager.aiAgents.knowledgeTab'), icon: BookOpen },
    { id: 'actions', label: t('manager.aiAgents.actionsTab'), icon: Zap },
    { id: 'software', label: t('manager.connected.title', 'Logiciels connectés'), icon: Plug },
  ]

  const isSubmitting = formState.isSubmitting || createAgent.isPending || updateAgent.isPending

  // Mapping champ → tab pour naviguer automatiquement vers l'erreur.
  // Sans ça, l'utilisateur clique 'Mettre à jour' et rien ne se passe si
  // un champ requis (ex: system_prompt) est vide dans un onglet inactif.
  const FIELD_TO_TAB: Record<string, TabType> = {
    name: 'behavior',
    voice_id: 'behavior',
    first_message: 'behavior',
    system_prompt: 'behavior',
    business_context: 'behavior',
    knowledge_web_urls: 'knowledge',
    knowledge_text: 'knowledge',
    sms_actions: 'actions',
    transfer_actions: 'actions',
    notify_admin_actions: 'actions',
  }

  const onFormError = (errors: Record<string, unknown>) => {
    const firstField = Object.keys(errors)[0]
    const targetTab = firstField ? FIELD_TO_TAB[firstField] ?? 'behavior' : 'behavior'
    if (activeTab !== targetTab) {
      setActiveTab(targetTab)
    }
    toast.error(
      t(
        'manager.aiAgents.formInvalidToast',
        'Certains champs requis sont vides ou invalides. Vérifie les sections marquées en rouge.',
      ),
    )
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit, onFormError)}
      className="h-full bg-gray-50 dark:bg-slate-900 flex flex-col overflow-hidden"
    >
      {/* Header sticky */}
      <div className="bg-white dark:bg-slate-900 border-b dark:border-slate-800 px-3 sm:px-6 py-3 sm:py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 flex-shrink-0 z-10">
        <div className="flex items-center gap-2 sm:gap-4 min-w-0">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onCancel}
            disabled={isSubmitting}
            className="gap-2 flex-shrink-0"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline">{t('manager.aiAgents.back')}</span>
          </Button>
          <h1 className="text-base sm:text-xl font-semibold truncate">
            {agent
              ? t('manager.aiAgents.editVoiceAgent')
              : t('manager.aiAgents.createVoiceAgent')}
          </h1>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          {/* Édition seulement : un agent qui n'existe pas encore n'a rien à
              tester. Placé à côté d'« Enregistrer » car c'est le geste qui
              suit naturellement une modification. */}
          {agent && (
            <Button type="button" variant="ghost" size="icon" disabled={isSubmitting} onClick={() => setHistorique(true)}
              title={t('manager.aiAgents.revisions.title', 'Historique des modifications')} className="text-slate-500 flex-shrink-0">
              <History className="h-4 w-4" />
            </Button>
          )}
          {agent && (
            // « Tester Mira » plutôt que « Tester » : le bouton dit ce qui va
            // se passer. AudioLines et non une icône téléphone : dans ce
            // produit, le téléphone désigne les appels humains — ce qui
            // distingue l'agent IA, c'est la voix.
            <Button
              type="button"
              variant="outline"
              disabled={isSubmitting}
              onClick={() => void handleSubmit(onSaveAndTest, onFormError)()}
              title={t('manager.aiAgents.testCall.formButtonTitle', {
                name: watch('name') || agent.name,
              })}
              className="gap-2 text-sm sm:text-base flex-1 sm:flex-none text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-900 hover:bg-blue-50 dark:hover:bg-blue-950/40 hover:text-blue-800"
            >
              <AudioLines className="h-4 w-4" />
              {t('manager.aiAgents.testCall.formButtonTitle', {
                name: truncateName(watch('name') || agent.name),
              })}
            </Button>
          )}
          <Button
            type="submit"
            disabled={isSubmitting}
            className="bg-blue-600 hover:bg-blue-700 text-sm sm:text-base flex-1 sm:flex-none"
          >
            {isSubmitting
              ? t('manager.aiAgents.saving')
              : agent
                ? t('manager.aiAgents.update')
                : t('manager.aiAgents.createTheAgent')}
          </Button>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row flex-1 min-h-0">
        {/* Sidebar desktop */}
        <div className="hidden lg:block lg:w-80 bg-white dark:bg-slate-900 border-r dark:border-slate-800 overflow-y-auto">
          <div className="p-6 space-y-6">
            <SidebarFields
              control={control}
              watch={watch}
              setValue={setValue}
              errors={formState.errors}
              selectedVoice={selectedVoice}
              expressiveVoice={selectedExpressiveVoice}
              companyMainNumber={companyMainNumber}
              phoneNumbers={phoneNumbers}
              smsFromNumbers={smsFromNumbers}
              isEditMode={!!agent?.id}
              onOpenVoice={() => setIsVoiceModalOpen(true)}
              onOpenPhone={() => setIsPhoneModalOpen(true)}
            />
          </div>
        </div>

        {/* Contenu principal */}
        <div className="flex-1 overflow-y-auto">
          {/* Tabs nav */}
          <div className="bg-white dark:bg-slate-900 border-b dark:border-slate-800 px-3 sm:px-6">
            <div className="flex gap-1 overflow-x-auto">
              {tabs.map((tab) => {
                const Icon = tab.icon
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => !tab.disabled && setActiveTab(tab.id)}
                    disabled={tab.disabled}
                    className={cn(
                      'flex items-center gap-2 px-3 sm:px-4 py-2.5 sm:py-3 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap',
                      activeTab === tab.id
                        ? 'border-blue-600 text-blue-600'
                        : 'border-transparent text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white',
                      tab.disabled && 'opacity-50 cursor-not-allowed',
                    )}
                  >
                    <Icon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                    <span className="hidden sm:inline">{tab.label}</span>
                    <span className="sm:hidden">{tab.label.slice(0, 4)}</span>
                    {tab.disabled && (
                      <span className="hidden sm:inline text-xs text-gray-400">
                        {t('manager.aiAgents.comingSoon')}
                      </span>
                    )}
                  </button>
                )
              })}
            </div>
          </div>

          {/* Contenu de l'onglet actif */}
          <div className="p-3 sm:p-6">
            {activeTab === 'behavior' && (
              <div className="max-w-4xl space-y-6">
                {/* Sidebar mobile — affichée dans Behavior uniquement (1:1 Next) */}
                <div className="lg:hidden space-y-6 bg-white dark:bg-slate-900 p-4 rounded-lg border dark:border-slate-800">
                  <SidebarFields
                    control={control}
                    watch={watch}
                    setValue={setValue}
                    errors={formState.errors}
                    selectedVoice={selectedVoice}
                    expressiveVoice={selectedExpressiveVoice}
                    companyMainNumber={companyMainNumber}
                    phoneNumbers={phoneNumbers}
                    smsFromNumbers={smsFromNumbers}
                    isEditMode={!!agent?.id}
                    onOpenVoice={() => setIsVoiceModalOpen(true)}
                    onOpenPhone={() => setIsPhoneModalOpen(true)}
                  />
                </div>

                <div>
                  <h2 className="text-base sm:text-lg font-semibold mb-2">
                    {t('manager.aiAgents.agentIdentity')}
                  </h2>
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-slate-400 mb-4">
                    {t('manager.aiAgents.defineContextRole')}
                  </p>

                  <div className="space-y-6">
                    <div>
                      <Label
                        htmlFor="first_message"
                        className="text-sm font-medium text-gray-700 dark:text-slate-300 mb-2 block"
                      >
                        {t('manager.aiAgents.firstMessage')}{' '}
                        <span className="text-gray-500 font-normal">
                          ({t('manager.aiAgents.optional')})
                        </span>
                      </Label>
                      <Textarea
                        id="first_message"
                        placeholder={t('manager.aiAgents.firstMessagePlaceholder')}
                        rows={3}
                        {...form.register('first_message')}
                        className="mt-1 text-sm sm:text-base leading-relaxed resize-y bg-white dark:bg-slate-900 border-2 border-gray-200 dark:border-slate-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 rounded-lg px-3 sm:px-4 py-2 sm:py-3 transition-all"
                      />
                      <p className="text-xs text-gray-500 dark:text-slate-400 mt-2">
                        {t('manager.aiAgents.firstMessageDescription')}
                      </p>
                      {agent?.id && (
                        <GreetingPreview
                          companyId={companyId}
                          agentId={agent.id}
                          textChanged={
                            (watch('first_message') ?? '').trim() !== (agent.first_message ?? '').trim()
                          }
                          voiceChanged={voiceId !== agent.voice_id}
                        />
                      )}
                    </div>

                    <div>
                      <Label
                        htmlFor="system_prompt"
                        className="text-sm font-medium text-gray-700 dark:text-slate-300 mb-2 block"
                      >
                        {t('manager.aiAgents.personalityInstructions')}{' '}
                        <span className="text-gray-500 font-normal">
                          ({t('manager.aiAgents.agentRole')})
                        </span>
                      </Label>
                      <Textarea
                        id="system_prompt"
                        placeholder={t('manager.aiAgents.personalityPlaceholder')}
                        rows={10}
                        {...form.register('system_prompt')}
                        className="mt-1 text-sm sm:text-base leading-relaxed resize-y bg-white dark:bg-slate-900 border-2 border-gray-200 dark:border-slate-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 rounded-lg px-3 sm:px-4 py-2 sm:py-3 transition-all"
                      />
                      {formState.errors.system_prompt && (
                        <p className="text-xs text-red-500 mt-1">
                          {t(
                            `manager.aiAgents.errors.${formState.errors.system_prompt.message}`,
                          )}
                        </p>
                      )}
                      <p className="text-xs text-gray-500 dark:text-slate-400 mt-2">
                        {t('manager.aiAgents.defineRolePersonality')}
                      </p>
                    </div>

                  </div>
                </div>

              </div>
            )}

            {activeTab === 'knowledge' && (
              <KnowledgeTabContent
                companyId={companyId}
                agentId={agent?.id}
              />
            )}

            {activeTab === 'software' && (
              <div className="max-w-5xl">
                {agent?.id ? (
                  <ConnectedSoftwareView agentId={agent.id} />
                ) : (
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    {t('manager.connected.afterCreate', 'Enregistrez l’agent pour lui donner accès à vos logiciels.')}
                  </p>
                )}
              </div>
            )}

            {activeTab === 'actions' && (
              <div className="max-w-4xl space-y-2">
                {/* Une ligne par capacité, détail au clic (une seule ouverte). */}
                <CapaciteRow icone={Target} titre={t('manager.aiAgents.missions.title')} ouvert={capacite === 'missions'} onToggle={() => basculer('missions')}
                  etat={missionsAgent.length ? t('manager.aiAgents.capacites.missionsEtat', '{{on}} actives sur {{n}}', { on: missionsAgent.filter((m) => m.is_active).length, n: missionsAgent.length }) : t('manager.aiAgents.capacites.aucune', 'aucune')}>
                  <MissionsSection
                    companyId={companyId}
                    agentId={agent?.id}
                    hasCalendar={!!(agent?.google_calendar || agent?.microsoft_calendar || agent?.square_booking || agent?.wix_booking)}
                    sansEntete
                  />
                </CapaciteRow>

                <CapaciteRow icone={Table2} titre={t('manager.aiAgents.actions.lookupTitle', 'Tables de référence')} ouvert={capacite === 'tables'} onToggle={() => basculer('tables')}
                  etat={tablesAgent.length ? tablesAgent.map((x) => x.name).join(', ') : t('manager.aiAgents.capacites.aucune', 'aucune')}>
                  <LookupSection companyId={companyId} agentId={agent?.id} sansEntete />
                </CapaciteRow>

                <CapaciteRow icone={MessageSquare} titre={t('manager.aiAgents.actions.smsTitle')} ouvert={capacite === 'sms'} onToggle={() => basculer('sms')}
                  etat={smsActions.fields.length ? t('manager.aiAgents.capacites.n', '{{n}}', { n: smsActions.fields.length }) : t('manager.aiAgents.capacites.aucun', 'aucun')}>
                {/* SMS Actions */}
                <section>
                  <div className="flex justify-end mb-3">
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => setEditingSMS('new')}
                      className="gap-2 w-full sm:w-auto text-sm"
                    >
                      <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                      {t('manager.aiAgents.actions.addSms')}
                    </Button>
                  </div>
                  {smsActions.fields.length > 0 && (
                    <div className="border dark:border-slate-800 rounded-lg overflow-hidden bg-white dark:bg-slate-900 divide-y divide-gray-100 dark:divide-slate-800">
                      {smsActions.fields.map((field, index) => {
                        const action = field as unknown as SMSAction
                        return (
                          <div
                            key={field.id}
                            className="group flex items-center justify-between px-4 py-3 hover:bg-gray-50 dark:hover:bg-slate-800/50 transition-colors"
                          >
                            <div className="min-w-0 flex-1">
                              <span className="font-medium text-sm text-gray-900 dark:text-white truncate block">
                                {action.name}
                              </span>
                              <p className="text-xs text-gray-500 dark:text-slate-400 truncate mt-0.5">
                                {t('manager.aiAgents.actions.smsReplyLabel')}: "{action.agent_response}"
                              </p>
                            </div>
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                                onClick={() => setEditingSMS(action)}
                              >
                                <Edit2 className="h-3.5 w-3.5 text-gray-500" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                                onClick={() => smsActions.remove(index)}
                              >
                                <Trash2 className="h-3.5 w-3.5 text-red-500" />
                              </Button>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </section>
                </CapaciteRow>

                <CapaciteRow icone={PhoneForwarded} titre={t('manager.aiAgents.actions.transferTitle')} ouvert={capacite === 'transfert'} onToggle={() => basculer('transfert')}
                  etat={transferActions.fields.length ? t('manager.aiAgents.capacites.n', '{{n}}', { n: transferActions.fields.length }) : t('manager.aiAgents.capacites.aucun', 'aucun')}>
                {/* Transfer Actions */}
                <section>
                  <div className="flex justify-end mb-3">
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => setEditingTransfer('new')}
                      className="gap-2 w-full sm:w-auto text-sm"
                    >
                      <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                      {t('manager.aiAgents.actions.addTransfer')}
                    </Button>
                  </div>
                  {transferActions.fields.length > 0 && (
                    <div className="border dark:border-slate-800 rounded-lg overflow-hidden bg-white dark:bg-slate-900 divide-y divide-gray-100 dark:divide-slate-800">
                      {transferActions.fields.map((field, index) => {
                        const action = field as unknown as TransferAction
                        return (
                          <div
                            key={field.id}
                            className="group flex items-center justify-between px-4 py-3 hover:bg-gray-50 dark:hover:bg-slate-800/50 transition-colors"
                          >
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-medium text-sm text-gray-900 dark:text-white truncate">
                                  {action.name}
                                </span>
                                <span
                                  className={cn(
                                    'text-xs px-2 py-0.5 rounded-full shrink-0',
                                    action.transfer_mode === 'warm'
                                      ? 'bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-300'
                                      : 'bg-gray-100 text-gray-700 dark:bg-slate-800 dark:text-slate-300',
                                  )}
                                >
                                  {action.transfer_mode === 'warm'
                                    ? t('manager.aiAgents.actions.warm')
                                    : t('manager.aiAgents.actions.cold')}
                                </span>
                              </div>
                              <p className="text-xs text-gray-500 dark:text-slate-400 truncate mt-0.5">
                                {t('manager.aiAgents.actions.transferToLabel')}: {action.transfer_to_name || action.transfer_to}
                              </p>
                            </div>
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                                onClick={() => setEditingTransfer(action)}
                              >
                                <Edit2 className="h-3.5 w-3.5 text-gray-500" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                                onClick={() => transferActions.remove(index)}
                              >
                                <Trash2 className="h-3.5 w-3.5 text-red-500" />
                              </Button>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </section>
                </CapaciteRow>

                <CapaciteRow icone={Bell} titre={t('manager.aiAgents.actions.notifyTitle')} ouvert={capacite === 'prevenir'} onToggle={() => basculer('prevenir')}
                  etat={notifyActions.fields.length ? t('manager.aiAgents.capacites.n', '{{n}}', { n: notifyActions.fields.length }) : t('manager.aiAgents.capacites.aucune', 'aucune')}>
                {/* Notify Admin Actions */}
                <section>
                  <div className="flex justify-end mb-3">
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => setEditingNotify('new')}
                      className="gap-2 w-full sm:w-auto text-sm bg-amber-600 hover:bg-amber-700"
                    >
                      <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                      {t('manager.aiAgents.actions.addNotify')}
                    </Button>
                  </div>
                  {notifyActions.fields.length > 0 && (
                    <div className="border dark:border-slate-800 rounded-lg overflow-hidden bg-white dark:bg-slate-900 divide-y divide-gray-100 dark:divide-slate-800">
                      {notifyActions.fields.map((field, index) => {
                        const action = field as unknown as NotifyAdminAction
                        return (
                          <div
                            key={field.id}
                            className="group flex items-center justify-between px-4 py-3 hover:bg-gray-50 dark:hover:bg-slate-800/50 transition-colors"
                          >
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-medium text-sm text-gray-900 dark:text-white">
                                  {action.name}
                                </span>
                                <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-slate-300 shrink-0">
                                  {action.notification_method === 'both'
                                    ? t('manager.aiAgents.actions.methodBoth')
                                    : action.notification_method === 'email'
                                      ? t('manager.aiAgents.actions.methodEmail')
                                      : t('manager.aiAgents.actions.methodSms')}
                                </span>
                                <span
                                  className={cn(
                                    'text-xs px-2 py-0.5 rounded-full shrink-0',
                                    action.timing === 'realtime'
                                      ? 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300'
                                      : 'bg-gray-100 text-gray-700 dark:bg-slate-800 dark:text-slate-300',
                                  )}
                                >
                                  {action.timing === 'realtime'
                                    ? t('manager.aiAgents.actions.realtime')
                                    : t('manager.aiAgents.actions.postCall')}
                                </span>
                              </div>
                              <p className="text-xs text-gray-500 dark:text-slate-400 truncate mt-0.5">
                                {action.collect_fields.length > 0
                                  ? t('manager.aiAgents.actions.fieldsSummary', {
                                      count: action.collect_fields.length,
                                      names: action.collect_fields.map((f) => f.name).join(', '),
                                    })
                                  : action.scenario}
                              </p>
                            </div>
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                                onClick={() => setEditingNotify(action)}
                              >
                                <Edit2 className="h-3.5 w-3.5 text-gray-500" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                                onClick={() => notifyActions.remove(index)}
                              >
                                <Trash2 className="h-3.5 w-3.5 text-red-500" />
                              </Button>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </section>
                </CapaciteRow>

                <CapaciteRow icone={Calendar} titre={t('manager.aiAgents.calendar.title')} ouvert={capacite === 'calendrier'} onToggle={() => basculer('calendrier')}
                  attention={!(watch('google_calendar') || watch('microsoft_calendar') || watch('square_booking') || watch('wix_booking'))}
                  etat={watch('google_calendar') || watch('microsoft_calendar') || watch('square_booking') || watch('wix_booking') ? t('manager.aiAgents.capacites.calendrierLie', 'calendrier lié') : t('manager.aiAgents.capacites.aucunCalendrier', 'aucun calendrier')}>
                  <CalendarBookingConfig
                    control={control}
                    watch={watch}
                    setValue={setValue}
                    companyId={companyId}
                    sansEntete
                  />
                </CapaciteRow>
              </div>
            )}
          </div>
        </div>
      </div>

      <VoiceSelectionModal
        isOpen={isVoiceModalOpen && !modeExpressif}
        onClose={() => setIsVoiceModalOpen(false)}
        voices={voices}
        selectedVoiceId={voiceId}
        onSelectVoice={(id) => setValue('voice_id', id, { shouldDirty: true, shouldValidate: true })}
      />

      <ExpressiveVoiceModal
        isOpen={isVoiceModalOpen && modeExpressif}
        onClose={() => setIsVoiceModalOpen(false)}
        voices={expressiveVoices}
        selectedVoiceId={voixExpressive}
        agentLanguage={language}
        onSelectVoice={(id) => setValue('voix_expressive', id, { shouldDirty: true })}
      />

      <PhoneNumberSelectionModal
        isOpen={isPhoneModalOpen}
        onClose={() => setIsPhoneModalOpen(false)}
        phoneNumbers={phoneNumbers}
        companyMainNumber={companyMainNumber}
        selectedNumberId={phoneNumberId}
        onSelectNumber={(id) =>
          setValue('phone_number_id', id, { shouldDirty: true })
        }
        allowNone={!!agent}
      />

      {agent && (
        <AgentRevisionsSheet companyId={companyId} agentId={agent.id} open={historique} onOpenChange={setHistorique}
          onRestored={() => setHistorique(false)} />
      )}

      <SMSActionModal
        isOpen={editingSMS !== null}
        agentLanguage={language}
        onClose={() => setEditingSMS(null)}
        action={editingSMS === 'new' ? null : editingSMS}
        onSave={(saved) => {
          const idx = smsActions.fields.findIndex(
            (f) => (f as unknown as SMSAction).id === saved.id,
          )
          if (idx >= 0) smsActions.update(idx, saved)
          else smsActions.append(saved)
        }}
      />

      <TransferActionModal
        isOpen={editingTransfer !== null}
        agentLanguage={language}
        onClose={() => setEditingTransfer(null)}
        action={editingTransfer === 'new' ? null : editingTransfer}
        onSave={(saved) => {
          const idx = transferActions.fields.findIndex(
            (f) => (f as unknown as TransferAction).id === saved.id,
          )
          if (idx >= 0) transferActions.update(idx, saved)
          else transferActions.append(saved)
        }}
      />

      <NotifyAdminModal
        isOpen={editingNotify !== null}
        onClose={() => setEditingNotify(null)}
        action={editingNotify === 'new' ? null : editingNotify}
        agentLanguage={watch('language')}
        companyId={companyId}
        agentId={agent?.id}
        onSave={(saved) => {
          const idx = notifyActions.fields.findIndex(
            (f) => (f as unknown as NotifyAdminAction).id === saved.id,
          )
          if (idx >= 0) notifyActions.update(idx, saved)
          else notifyActions.append(saved)
        }}
      />

      {agent && showTestCall && (
        <TestCallModal
          isOpen={showTestCall}
          onClose={() => setShowTestCall(false)}
          companyId={companyId}
          agentId={agent.id}
          agentName={watch('name') || agent.name}
          saveState={testSaveState}
          onRetrySave={() => {
            if (testValuesRef.current) void runTestSave(testValuesRef.current)
          }}
        />
      )}
    </form>
  )
}

// =============================================================================
// Sidebar fields (réutilisé desktop + mobile, piloté par RHF)
// =============================================================================

const formatVoiceLabel = (key: 'gender' | 'accent', value: string) => {
  if (key === 'gender') {
    return value === 'male' ? 'Homme' : value === 'female' ? 'Femme' : value
  }
  return value === 'american' ? 'Américain' : value === 'british' ? 'Britannique' : value
}

/** The voice card in expressive mode: the voice really used, tagged "Expressive". */
function ExpressiveVoiceSummary({ voice }: { voice?: ExpressiveVoice }) {
  const { t } = useTranslation()
  const name = voice ? voice.prenom ?? t('manager.aiAgents.expressive.customVoice') : null
  const details = [
    voice?.genre && t(`manager.aiAgents.expressive.genders.${voice.genre}`),
    voice?.accent && t(`manager.aiAgents.expressive.accents.${voice.accent}`),
  ]
    .filter(Boolean)
    .join(' • ')
  return (
    <div className="flex items-center gap-3 overflow-hidden">
      <span
        aria-hidden
        className="h-10 w-10 rounded-full border bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200 flex items-center justify-center text-sm font-semibold flex-shrink-0"
      >
        {name ? name.charAt(0).toUpperCase() : <Mic className="h-4 w-4 text-gray-500" />}
      </span>
      <div className="min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <span className={cn('font-medium text-sm truncate', !name && 'text-gray-500 font-normal')}>
            {name ?? t('manager.aiAgents.selectVoice')}
          </span>
          <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200 dark:bg-blue-950/50 dark:text-blue-300 dark:ring-blue-900 flex-shrink-0">
            {t('manager.aiAgents.expressive.badge')}
          </span>
        </div>
        {details && <div className="text-xs text-gray-500 truncate">{details}</div>}
      </div>
    </div>
  )
}

interface SidebarFieldsProps {
  control: Control<FormValues>
  watch: UseFormWatch<FormValues>
  setValue: UseFormSetValue<FormValues>
  errors: FieldErrors<FormValues>
  selectedVoice?: ElevenLabsVoice
  expressiveVoice?: ExpressiveVoice
  companyMainNumber: string | null
  phoneNumbers: Array<{ id: number; phone_number: string }>
  smsFromNumbers: SmsFromNumberOption[]
  isEditMode: boolean
  onOpenVoice: () => void
  onOpenPhone: () => void
}

function SidebarFields({
  control,
  watch,
  setValue,
  errors,
  selectedVoice,
  expressiveVoice,
  companyMainNumber,
  phoneNumbers,
  smsFromNumbers,
  isEditMode,
  onOpenVoice,
  onOpenPhone,
}: SidebarFieldsProps) {
  const { t } = useTranslation()
  const callMode = watch('call_mode')
  const phoneNumberId = watch('phone_number_id')
  const smsFromNumber = watch('sms_from_number')
  const modeExpressif = watch('mode_expressif')
  // SidebarFields renders twice (desktop sidebar, phone layout): ids must differ.
  const expressiveSwitchId = `${useId()}-mode-expressif`

  // L'agent a-t-il un numéro dédié (ou main) ? Si oui, les SMS partent de là
  // et on n'a pas besoin du sélecteur SMS supplémentaire.
  const hasDedicatedNumber =
    !!phoneNumberId && phoneNumberId !== '' && phoneNumberId !== 'none'
  // Le sélecteur SMS est utile uniquement quand l'agent est routé via flow IVR
  // (donc pas de numéro dédié). On l'affiche en mode édition.
  const showSmsFromNumberSelector = isEditMode && callMode !== 'outbound' && !hasDedicatedNumber

  return (
    <>
      <div>
        <Label htmlFor="agent-name" className="text-sm font-medium">
          {t('manager.aiAgents.agentName')}
        </Label>
        <Controller
          control={control}
          name="name"
          render={({ field }) => (
            <Input
              id="agent-name"
              placeholder={t('manager.aiAgents.agentNamePlaceholder')}
              className="mt-2"
              {...field}
            />
          )}
        />
        {errors.name && (
          <p className="text-xs text-red-500 mt-1">
            {t(`manager.aiAgents.errors.${errors.name.message}`)}
          </p>
        )}
      </div>

      <div>
        <Label className="text-sm font-medium">{t('manager.aiAgents.callMode')}</Label>
        <Controller
          control={control}
          name="call_mode"
          render={({ field }) => (
            <Select
              value={field.value}
              onValueChange={(v: 'inbound' | 'outbound') => {
                field.onChange(v)
                // Si on passe à sortant, libérer le numéro pour cohérence.
                if (v === 'outbound') {
                  // setValue est inutile ici : le watch ne déclenchera pas d'update sur d'autres
                  // champs. On laisse phone_number_id tel quel ; il est ignoré au submit.
                }
              }}
            >
              <SelectTrigger className="mt-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="inbound">
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-blue-600 rotate-90" />
                    <span>{t('manager.aiAgents.inbound')}</span>
                  </div>
                </SelectItem>
                <SelectItem value="outbound">
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-green-600 -rotate-90" />
                    <span>{t('manager.aiAgents.outbound')}</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          )}
        />
        <p className="text-xs text-gray-500 mt-1">
          {callMode === 'outbound'
            ? t('manager.aiAgents.outboundDescription')
            : t('manager.aiAgents.inboundDescription')}
        </p>
      </div>

      <div>
        <Label className="text-sm font-medium">{t('manager.aiAgents.language')}</Label>
        <Controller
          control={control}
          name="language"
          render={({ field }) => (
            <Select value={field.value} onValueChange={field.onChange}>
              <SelectTrigger className="mt-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="fr">🇫🇷 Français</SelectItem>
                <SelectItem value="en">🇺🇸 Anglais</SelectItem>
                <SelectItem value="es">🇪🇸 Espagnol</SelectItem>
                <SelectItem value="de">🇩🇪 Allemand</SelectItem>
                <SelectItem value="fr-en">🇨🇦 {t('manager.aiAgents.languageFrEn', 'Français et anglais')}</SelectItem>
                <SelectItem value="en-fr">🇨🇦 {t('manager.aiAgents.languageEnFr', 'Anglais et français')}</SelectItem>
                {field.value === 'multi' && (
                  <SelectItem value="multi">🌍 {t('manager.aiAgents.languageMultilingual', 'Multilingue')}</SelectItem>
                )}
              </SelectContent>
            </Select>
          )}
        />
      </div>

      <div>
        <div className="flex items-center justify-between gap-3">
          <Label className="text-sm font-medium">{t('manager.aiAgents.voice')}</Label>
          <div className="flex items-center gap-2">
            <Label
              htmlFor={expressiveSwitchId}
              className="text-xs font-normal text-gray-600 dark:text-slate-300 cursor-pointer py-1"
            >
              {t('manager.aiAgents.expressive.toggle')}
            </Label>
            <Controller
              control={control}
              name="mode_expressif"
              render={({ field }) => (
                <Switch
                  id={expressiveSwitchId}
                  checked={field.value}
                  onCheckedChange={(checked) => field.onChange(checked)}
                />
              )}
            />
          </div>
        </div>
        <button
          type="button"
          onClick={onOpenVoice}
          className="mt-2 w-full border rounded-lg p-3 cursor-pointer hover:border-blue-400 bg-white dark:bg-slate-900 flex items-center justify-between group transition-colors text-left"
        >
          {modeExpressif ? (
            <ExpressiveVoiceSummary voice={expressiveVoice} />
          ) : selectedVoice ? (
            <div className="flex items-center gap-3 overflow-hidden">
              <Avatar className="h-10 w-10 border flex-shrink-0">
                <AvatarImage
                  src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${selectedVoice.name}`}
                />
                <AvatarFallback>
                  <User className="h-5 w-5 text-gray-500" />
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <div className="font-medium text-sm truncate">{selectedVoice.name}</div>
                {(selectedVoice.labels?.gender || selectedVoice.labels?.accent) && (
                <div className="text-xs text-gray-500 truncate">
                  {selectedVoice.labels?.gender
                    ? formatVoiceLabel('gender', selectedVoice.labels.gender)
                    : 'IA'}{' '}
                  •{' '}
                  {selectedVoice.labels?.accent
                    ? formatVoiceLabel('accent', selectedVoice.labels.accent)
                    : 'Standard'}
                </div>
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Mic className="h-4 w-4" />
              {t('manager.aiAgents.selectVoice')}
            </div>
          )}
          <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-blue-500 flex-shrink-0" />
        </button>
        {errors.voice_id && (
          <p className="text-xs text-red-500 mt-1">
            {t(`manager.aiAgents.errors.${errors.voice_id.message}`)}
          </p>
        )}
      </div>

      {callMode !== 'outbound' && (
        <div>
          <Label className="text-sm font-medium">
            {t('manager.aiAgents.phoneNumber')}
          </Label>
          <button
            type="button"
            onClick={onOpenPhone}
            className="mt-2 w-full border rounded-lg p-3 cursor-pointer hover:border-blue-400 bg-white dark:bg-slate-900 flex items-center justify-between group transition-colors text-left"
          >
            {phoneNumberId ? (
              <div className="flex items-center gap-3 overflow-hidden">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 flex-shrink-0">
                  <Phone className="h-5 w-5 text-blue-600" />
                </div>
                <div className="min-w-0">
                  <div className="font-medium text-sm truncate">
                    {phoneNumberId === 'main'
                      ? companyMainNumber
                      : phoneNumberId === 'none'
                        ? t('manager.aiAgents.noNumberOption')
                        : phoneNumbers.find((n) => n.id.toString() === phoneNumberId)
                            ?.phone_number}
                  </div>
                  <div className="text-xs text-gray-500 truncate">
                    {phoneNumberId === 'main' && t('manager.aiAgents.mainNumber')}
                    {phoneNumberId === 'none' && t('manager.aiAgents.notAssigned')}
                    {phoneNumberId !== 'main' &&
                      phoneNumberId !== 'none' &&
                      t('manager.aiAgents.dedicatedNumber')}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Phone className="h-4 w-4" />
                {t('manager.aiAgents.selectNumberOptional')}
              </div>
            )}
            <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-blue-500 flex-shrink-0" />
          </button>
          {phoneNumbers.length === 0 && !companyMainNumber && (
            <p className="text-xs text-gray-500 mt-1">
              {t('manager.aiAgents.buyNumberFirst')}
            </p>
          )}
        </div>
      )}

      {showSmsFromNumberSelector && (
        <div>
          <Label className="text-sm font-medium">
            {t('manager.aiAgents.smsFromNumber.label')}
          </Label>
          <p className="text-xs text-gray-500 dark:text-slate-400 mt-1 mb-2">
            {t('manager.aiAgents.smsFromNumber.help')}
          </p>
          <Select
            value={smsFromNumber || '__none__'}
            onValueChange={(value) =>
              setValue('sms_from_number', value === '__none__' ? '' : value, {
                shouldDirty: true,
              })
            }
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder={t('manager.aiAgents.smsFromNumber.placeholder')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">
                {t('manager.aiAgents.smsFromNumber.none')}
              </SelectItem>
              {smsFromNumbers.map((opt) => (
                <SelectItem key={opt.phone_number} value={opt.phone_number}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {!smsFromNumber && smsFromNumbers.length > 0 && (
            <div className="mt-3 flex gap-2 rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-900/20 p-3">
              <AlertTriangle className="h-4 w-4 flex-shrink-0 text-amber-600 dark:text-amber-400 mt-0.5" />
              <div className="text-xs text-amber-900 dark:text-amber-200">
                <p className="font-semibold mb-1">
                  {t('manager.aiAgents.smsFromNumber.warningTitle')}
                </p>
                <p>{t('manager.aiAgents.smsFromNumber.warningDesc')}</p>
              </div>
            </div>
          )}
          {smsFromNumbers.length === 0 && (
            <p className="text-xs text-gray-500 mt-2">
              {t('manager.aiAgents.smsFromNumber.noNumbers')}
            </p>
          )}
        </div>
      )}

      {callMode === 'outbound' && (
        <div>
          <Label className="text-sm font-medium">
            {t('manager.aiAgents.phoneNumber')}
          </Label>
          <div className="mt-2 border rounded-lg p-3 bg-gray-50 dark:bg-slate-800">
            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-slate-400">
              <Phone className="h-4 w-4 text-gray-400" />
              <span>{t('manager.aiAgents.numberAssignedByCampaign')}</span>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
